import React from 'react';
import { Box, Button, Divider, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import type { HouseholdModel, Income, Leave, Person } from '../../services/household-retirement-service';
import { DateField, NumField, produce } from './fields';

const slug = (name: string, taken: Set<string>) => {
  const base = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'person';
  let id = base;
  for (let i = 2; taken.has(id); i++) id = `${base}-${i}`;
  return id;
};

/**
 * People, their incomes and leave periods. Adults carry a retirement age,
 * CPP (estimated from earnings, or a statement figure) and OAS settings;
 * each adult's salary may have leaves: an employer top-up phase, then EI
 * only, then a return date.
 */
export const PeopleEditor: React.FC<{ model: HouseholdModel; onChange: (m: HouseholdModel) => void }> = ({ model, onChange }) => {
  const set = (fn: (d: HouseholdModel) => void) => onChange(produce(model, fn));

  const addPerson = (role: 'adult' | 'child') => set((d) => {
    const taken = new Set(d.people.map((p) => p.id));
    const name = role === 'adult' ? `Adult ${d.people.filter((p) => p.role === 'adult').length + 1}`
      : `Child ${d.people.filter((p) => p.role === 'child').length + 1}`;
    const id = slug(name, taken);
    d.people.push(role === 'adult'
      ? { id, name, dob: '1990-01-01', role, retirement_age: 65, cpp: { mode: 'estimate', start_age: 65 }, oas: { start_age: 65, residency_years: 40 } }
      : { id, name, dob: new Date().toISOString().slice(0, 10), role });
    if (role === 'adult') d.incomes.push({ id: `${id}-salary`, person_id: id, label: 'Salary', annual_gross: 0, leaves: [] });
  });

  const removePerson = (id: string) => set((d) => {
    d.people = d.people.filter((p) => p.id !== id);
    d.incomes = d.incomes.filter((i) => i.person_id !== id);
    for (const k of ['childcare', 'activities', 'post_secondary'] as const) {
      d.expenses[k] = d.expenses[k].filter((c) => c.person_id !== id);
    }
  });

  const updPerson = (i: number, fn: (p: Person) => void) => set((d) => fn(d.people[i]));
  const updIncome = (id: string, fn: (inc: Income) => void) => set((d) => {
    const inc = d.incomes.find((x) => x.id === id);
    if (inc) fn(inc);
  });

  return (
    <Stack spacing={2} data-testid="people-editor">
      {model.people.map((p, i) => (
        <Box key={p.id} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }} data-testid={`person-${p.id}`}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-start' }} flexWrap="wrap" useFlexGap>
            <TextField size="small" label="Name" value={p.name} inputProps={{ 'aria-label': `Name of ${p.id}` }}
              onChange={(e) => updPerson(i, (x) => { x.name = e.target.value; })} />
            <DateField label="Date of birth" value={p.dob} onChange={(v) => updPerson(i, (x) => { x.dob = v; })}
              inputProps={{ 'aria-label': `Date of birth of ${p.id}` }} />
            <TextField select size="small" label="Role" value={p.role} sx={{ minWidth: 110 }}
              onChange={(e) => updPerson(i, (x) => { x.role = e.target.value as Person['role']; })}>
              <MenuItem value="adult">Adult</MenuItem>
              <MenuItem value="child">Child</MenuItem>
            </TextField>
            {p.role === 'adult' && (
              <>
                <NumField label="Retire at age" value={p.retirement_age ?? 65} sx={{ width: 130 }}
                  inputProps={{ 'aria-label': `Retirement age of ${p.id}` }}
                  onChange={(v) => updPerson(i, (x) => { x.retirement_age = v ?? 65; })} />
                <TextField select size="small" label="CPP" value={p.cpp?.mode ?? 'estimate'} sx={{ minWidth: 230 }}
                  onChange={(e) => updPerson(i, (x) => { x.cpp = { ...(x.cpp ?? {}), mode: e.target.value as 'estimate' | 'amount' }; })}>
                  <MenuItem value="estimate">Estimate from earnings</MenuItem>
                  <MenuItem value="amount">Statement amount</MenuItem>
                </TextField>
                {p.cpp?.mode === 'amount' && (
                  <NumField label="CPP $/mo at 65" value={p.cpp.monthly_at_65} sx={{ width: 160 }}
                    onChange={(v) => updPerson(i, (x) => { x.cpp = { ...x.cpp!, monthly_at_65: v ?? 0 }; })} />
                )}
                <NumField label="CPP start age" value={p.cpp?.start_age ?? 65} sx={{ width: 140 }}
                  onChange={(v) => updPerson(i, (x) => { x.cpp = { ...(x.cpp ?? { mode: 'estimate' }), start_age: v ?? 65 }; })} />
                <NumField label="OAS start age" value={p.oas?.start_age ?? 65} sx={{ width: 140 }}
                  onChange={(v) => updPerson(i, (x) => { x.oas = { ...(x.oas ?? {}), start_age: v ?? 65 }; })} />
                <NumField label="Share of joint %" value={p.joint_share_pct ?? null} placeholder="50" sx={{ width: 190 }}
                  helperText="their own plan"
                  inputProps={{ 'aria-label': `Joint share of ${p.id}` }}
                  onChange={(v) => updPerson(i, (x) => { x.joint_share_pct = v; })} />
                <NumField label="Share of spending %" value={p.spending_share_pct ?? null} placeholder="50" sx={{ width: 210 }}
                  helperText="their own plan"
                  inputProps={{ 'aria-label': `Spending share of ${p.id}` }}
                  onChange={(v) => updPerson(i, (x) => { x.spending_share_pct = v; })} />
                <NumField label="Years in Canada (OAS)" value={p.oas?.residency_years ?? 40} sx={{ width: 200 }}
                  helperText="40 after age 18 = full OAS"
                  onChange={(v) => updPerson(i, (x) => { x.oas = { ...(x.oas ?? {}), residency_years: v ?? 40 }; })} />
              </>
            )}
            <Box sx={{ flex: 1 }} />
            <Tooltip title={`Remove ${p.name}`}>
              <IconButton size="small" aria-label={`Remove ${p.name}`} onClick={() => removePerson(p.id)}><DeleteIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>

          {p.role === 'adult' && model.incomes.filter((inc) => inc.person_id === p.id).map((inc) => (
            <Box key={inc.id} sx={{ mt: 1.5, pl: { md: 2 } }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
                <TextField size="small" label="Income" value={inc.label ?? ''} onChange={(e) => updIncome(inc.id, (x) => { x.label = e.target.value; })} />
                <NumField label="Gross salary $/yr" value={inc.annual_gross} sx={{ width: 180 }}
                  inputProps={{ 'aria-label': `Salary of ${p.id}` }}
                  onChange={(v) => updIncome(inc.id, (x) => { x.annual_gross = v ?? 0; })} />
                <NumField label="Growth %/yr" value={inc.growth_pct ?? null} placeholder={String(model.assumptions.salary_growth_pct)}
                  helperText="blank = plan default" sx={{ width: 150 }}
                  onChange={(v) => updIncome(inc.id, (x) => { x.growth_pct = v; })} />
                <DateField label="Starts" value={inc.start_date} onChange={(v) => updIncome(inc.id, (x) => { x.start_date = v || undefined; })} />
                <DateField label="Ends" value={inc.end_date} helperText="retirement ends it anyway"
                  onChange={(v) => updIncome(inc.id, (x) => { x.end_date = v || undefined; })} />
                <Button size="small" startIcon={<AddIcon />} onClick={() => updIncome(inc.id, (x) => {
                  x.leaves = [...(x.leaves ?? []), { label: 'Parental leave', start: new Date().toISOString().slice(0, 10),
                    topup_pct: 93, ei_biweekly: 0, return: new Date(Date.now() + 365 * 864e5).toISOString().slice(0, 10) }];
                })}>Leave</Button>
              </Stack>
              {(inc.leaves ?? []).map((l, li) => {
                const upd = (fn: (x: Leave) => void) => updIncome(inc.id, (x) => fn(x.leaves![li]));
                return (
                  <Box key={li} data-testid={`leave-${p.id}-${li}`} sx={{ mt: 1, ml: { md: 2 }, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {l.label || 'Leave'}: employer top-up, then EI only, then back to full salary
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                      <DateField label="Leave starts" value={l.start} onChange={(v) => upd((x) => { x.start = v; })} />
                      <DateField label="Top-up ends" value={l.topup_end} onChange={(v) => upd((x) => { x.topup_end = v || undefined; })} />
                      <NumField label="Top-up to % of salary" value={l.topup_pct} sx={{ width: 190 }} helperText="EI included"
                        onChange={(v) => upd((x) => { x.topup_pct = v ?? 0; })} />
                      <NumField label="or fixed $/mo top-up" value={l.topup_amount_monthly} sx={{ width: 190 }} helperText="EI added on top"
                        onChange={(v) => upd((x) => { x.topup_amount_monthly = v ?? undefined; })} />
                      <NumField label="EI $ biweekly" value={l.ei_biweekly} sx={{ width: 150 }}
                        inputProps={{ 'aria-label': `EI biweekly for ${p.id}` }}
                        onChange={(v) => upd((x) => { x.ei_biweekly = v ?? 0; })} />
                      <DateField label="Returns to work" value={l.return} onChange={(v) => upd((x) => { x.return = v; })}
                        inputProps={{ 'aria-label': `Return date for ${p.id}` }} />
                      <IconButton size="small" aria-label="Remove leave"
                        onClick={() => updIncome(inc.id, (x) => { x.leaves = x.leaves!.filter((_, k) => k !== li); })}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      ))}
      <Divider />
      <Stack direction="row" spacing={1}>
        <Button size="small" startIcon={<AddIcon />} onClick={() => addPerson('adult')}>Add adult</Button>
        <Button size="small" startIcon={<AddIcon />} onClick={() => addPerson('child')}>Add child</Button>
      </Stack>
    </Stack>
  );
};
